import { css, cx } from '@linaria/core'
import { t } from 'i18next'
import { type FC, Fragment, type ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { EnhancedManCode } from '#api/enhancements/enhancedTmsApiTypes'
import type { UpdatePatch } from '#api/tmsApiService'
import { Checkbox } from '#components/Checkbox'
import { HoverTooltip } from '#components/HoverTooltip'
import { Icon } from '#components/Icon'
import { NotAvailable } from '#components/tabularOverlay/EntryHelpers'
import { type GroupedFPCChange, useEnrichedJournalEntries } from '#features/ManCode/useEnrichedJournalEntries'
import ChangeIcon from '#icons/arrow-right-16.svg'
import ChevronRightIcon from '#icons/chevron-1-right-16.svg'
import RemoveIcon from '#icons/delete-16.svg'
import EditIcon from '#icons/edit-16.svg'
import AddIcon from '#icons/plus-16.svg'
import UnknownIcon from '#icons/questionmark.svg'
import ReplaceIcon from '#icons/reload-16.svg'
import ExternalIcon from '#icons/special-workflow.svg'
import UserIcon from '#icons/user-16.svg'
import { HistoryDetailList } from '#pages/Configuration/HistoryComponents/HistoryDetailList'
import { fragments, style } from '#styles'
import { useCurrencyFormatter } from '#utils/formatCurrency'
import { formatDuration } from '#utils/formatDuration'
import { testIds } from '#utils/testIds'

interface ManCodeJournalTabProps {
  manCode: EnhancedManCode
}

const journalTabStyle = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  overflow: hidden;
  min-height: 300px;

  > section {
    height: 100%;
    overflow-y: auto;
    padding-bottom: 24px;

    > p {
      grid-column: 1 / -1;
      padding: 16px;
      color: ${style.theme.textSoft};
    }
    
    code {
      white-space: break-spaces;
    }

    > header, > ._row {
      display: grid;
      grid-template-columns: subgrid;
      grid-column: 1 / -1;
      padding: 10px 0;
      border-bottom: 1px solid ${style.theme.borderSurface};

      > span {
        display: inline-flex;
        align-items: center;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;

        &:first-child {
          padding-left: 8px;
        }

        &:last-child {
          padding-right: 8px;
        }
      }
    }

    > header {
      color: ${style.theme.textSoft};
      padding-top: 0;
      padding-bottom: 6px;
      ${fragments.textStyle.body.xs.regular}
      position: sticky;
      top: 0;
      background-color: ${style.theme.backgroundSurfacePrimary};
      z-index: 1;

      > *:last-child {
        padding-left: 8px;
      }
    }

    > ._row {
      ${fragments.textStyle.body.s.regular}
    }
  }

  > ._entry-list {
    display: grid;
    justify-content: stretch;
    align-content: flex-start;
    grid-template-columns: 60px 16px minmax(60px, min-content) 40px auto auto;
    grid-column-gap: 8px;
    overflow-y: auto;

    > ._row {
      cursor: pointer;
      padding: 0;
      transition: background-color 0.2s ease;

      > * {
        padding: 10px 0;
      }
      
      ._changes-icon {
        transition: opacity 0.2s ease;
        opacity: 0;
      }

      > ._with-chevron {
        display: inline-flex;
        gap: 8px;
        justify-content: space-between;
      }

      &:not(:has(._history-entry)) > ._with-chevron {
        grid-column-end: span 2;
      }

      &._active {
        color: ${style.theme.textAccent};
      }

      &._active:not(:has(._history-entry._active)) {
        background-color: ${style.theme.backgroundSurfaceSecondary};

        ._base-changes {
          opacity: 1;
        }
      }

      &:hover:not(:has(._history-entry:hover)) {
        background-color: ${style.theme.backgroundSurfaceSecondary};

        > ._history-entry {
          background-color: ${style.theme.backgroundSurfacePrimary};
        }

        ._base-changes {
          opacity: 1;
        }
      }

      > ._history-entry {
        cursor: pointer;
        height: 100%;
        min-width: 120px;
        padding-left: 8px;
        justify-content: stretch;
        display: inline-flex;
        align-items: center;
        border-radius: 4px;
        transition: background-color 0.2s ease;
        background-color: ${style.theme.backgroundSurfacePrimary};

        > ._maic-stats {
          display: inline-flex;
          flex: 1;
          gap: 8px;
          justify-content: space-between;

          > ._change-count {
            ${fragments.textStyle.body.s.condensedBold}
          }
        }

        &._active {
          background-color: ${style.theme.backgroundSurfaceSecondary};

          > ._maic-changes {
            opacity: 1;
          }
        }

        &:hover {
          background-color: ${style.theme.backgroundSurfaceSecondary};

          > ._maic-changes {
            opacity: 1;
          }
        }
      }
    }
  }

  min-width: 800px;

  > ._details {
    flex: 1;

    display: grid;
    grid-template-columns: min-content auto auto auto min-content auto auto auto;
    grid-column-gap: 8px;
    justify-content: stretch;
    align-content: flex-start;


    > ._row {
      ._change-cell._highlighted {
        color: ${style.theme.textAccent};
      }

      ._id {
        opacity: 0.7;
      }

      ._change-type {
        display: inline-flex;
        justify-content: center;
        align-items: center;
        color: ${style.theme.textSoft};

        &._with-color {
          border-radius: 4px;
          padding: 2px;

          color: ${style.theme.textDefault};
          background-color: ${style.theme.backgroundSurfaceTertiaryStrong};

          ${fragments.textStyle.body.xs.condensedBold}
          &._type-replace {
            color: ${style.colors.yellow['900']};
            background-color: ${style.colors.yellow['100']};
          }

          &._type-add {
            color: ${style.colors.green['900']};
            background-color: ${style.colors.green['100']};
          }

          &._type-remove {
            color: ${style.colors.red['900']};
            background-color: ${style.colors.red['100']};
          }

          &._type-update {
            color: ${style.colors.blue['900']};
            background-color: ${style.colors.blue['100']};
          }
        }
      }
    }

    > ._detailed-change-entry {
      grid-column: 1 / -1;
      padding: 0 12px 8px 0;
      margin-bottom: 24px;

      > div {
        padding: 0;
        overflow: hidden;
        
        > ._heading:first-child {
          padding-top: 0;
        }
        
        > ._end {
          display: none;
        }
      }
    }
  }
`

function mapTypeToIcon(type: string | undefined) {
  switch (type) {
    case 'add':
      return AddIcon
    case 'remove':
      return RemoveIcon
    case 'replace':
      return ReplaceIcon
    default:
      return UnknownIcon
  }
}

function mapSystemToIcon(system: string | undefined) {
  switch (system) {
    case 'MAIC':
      return UserIcon
    default:
      return ExternalIcon
  }
}

// get a readable patch value
function pv(patch: UpdatePatch | null | undefined): ReactNode {
  if (!patch) {
    return undefined
  }

  if ('value' in patch) {
    if (patch.value === undefined || patch.value === null) {
      return <NotAvailable />
    }

    if (typeof patch.value === 'object') {
      return (
        <HoverTooltip.span title={JSON.stringify(patch.value, null, 2)}>
          {t('man-code.information-overlay.journal.object-value', 'Object')}
        </HoverTooltip.span>
      )
    }

    if (typeof patch.value === 'boolean') {
      return patch.value ? t('shared:common.label.yes', 'Yes') : t('shared:common.label.no', 'No')
    }

    return String(patch.value)
  }

  return '-'
}

export const FPCCodeChangeEntry: FC<{ change: GroupedFPCChange }> = ({ change }) => {
  const { t } = useTranslation()
  return (
    <div className={cx('_row _fpc-change-entry')} data-testid={testIds.journal.entries.fpc}>
      <span>
        <HoverTooltip.span
          className={cx('_change-type', '_type-update')}
          title={t('man-code.information-overlay.journal.fpc-change', 'Updated Factory Code')}
        >
          <Icon iconUrl={EditIcon} size="xs" />
        </HoverTooltip.span>
      </span>
      <span className="_id">{pv(change.fields.code?.from) ?? pv(change.fields.variant?.from) ?? <code>{change.path?.replace('/', '/​')}</code>}</span>
      <span>{pv(change.fields.familyDescription?.from) ?? <NotAvailable />}</span>
      <span>{pv(change.fields.variantDescription?.from) ?? <NotAvailable />}</span>

      <span className="_change-cell _highlighted">
        <Icon iconUrl={ChangeIcon} size="default" />
      </span>
      <span className="_id">{pv(change.fields.code?.to) ?? pv(change.fields.variant?.to) ?? <NotAvailable />}</span>
      <span>{pv(change.fields.familyDescription?.to) ?? <NotAvailable />}</span>
      <span>{pv(change.fields.variantDescription?.to) ?? <NotAvailable />}</span>
      {/*<pre>{JSON.stringify(change, null, 2)}</pre>*/}
    </div>
  )
}

export const ManCodeJournalTab: FC<ManCodeJournalTabProps> = ({ manCode }) => {
  const { t } = useTranslation()
  const [collapseFpcChanges, setCollapseFpcChanges] = useState(false)
  const { formatDeltaCurrency } = useCurrencyFormatter()
  const entries = useEnrichedJournalEntries(manCode, {
    collapseFpcChanges,
  })
  const [selection, setSelection] = useState<{
    index: number
    showChanges: boolean
  } | null>({
    index: entries.length - 1,
    showChanges: entries[entries.length - 1]?.changes.length !== 0,
  })

  if (entries.length === 0) {
    return (
      <main className="_content">
        <p>{t('man-code.information-overlay.journal.no-entries', 'No journal entries available.')}</p>
      </main>
    )
  }

  const activeEntry = selection !== null ? entries.find((e) => e.entryNumber === selection.index) : null

  return (
    <>
      <main className={cx(journalTabStyle, '_content')}>
        <section className="_entry-list">
          <header>
            <span>{t('man-code.information-overlay.journal.version', 'Version')}</span>
            <span>
              <Icon iconUrl={UnknownIcon} title={t('man-code.information-overlay.journal.system', 'System')} size="default" />
            </span>
            <span>{t('man-code.information-overlay.journal.updating-user', 'User')}</span>
            <span>
              <Icon iconUrl={EditIcon} title={t('man-code.information-overlay.journal.changes', 'Changes')} size="default" />
            </span>
            <span>{t('man-code.information-overlay.journal.updated', 'Updated At')}</span>
            <span>{t('man-code.information-overlay.journal.maic-changes', 'MAIC Changes')}</span>
          </header>
          {entries.map((entry, index) => {
            const hasMaicChanges = entry.changes.length > 0
            return (
              <Fragment key={entry.entryNumber ?? index}>
                <div
                  className={cx('_row', entry.entryNumber === selection?.index ? '_active' : '')}
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    setSelection({ index: entry.entryNumber ?? 0, showChanges: false })
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      setSelection({ index: entry.entryNumber ?? 0, showChanges: false })
                    }
                  }}
                  data-testid={testIds.journal.versionEntry}
                >
                  <span>v{entry.entryNumber ?? index}</span>
                  <span>
                    <Icon iconUrl={mapSystemToIcon(entry.updatingSystem)} title={entry.updatingSystem} size="default" />
                  </span>
                  <span>{entry.updatingUser}</span>
                  <span>{entry.forwardUpdateOperations?.length ?? 0}</span>
                  <span className="_with-chevron">
                    <span>
                      {entry.updateTimestamp
                        ? formatDuration(Date.now() - new Date(entry.updateTimestamp).getTime(), t)
                        : t('shared:common.label.unknown')}
                    </span>

                    <Icon
                      iconUrl={ChevronRightIcon}
                      size="default"
                      className={cx(
                        '_changes-icon _base-changes',
                        entry.entryNumber === selection?.index && !selection?.showChanges ? '_active' : '',
                      )}
                    />
                  </span>
                  {hasMaicChanges && (
                    <span
                      className={cx(
                        '_history-entry',
                        '_with-chevron',
                        entry.entryNumber === selection?.index && selection?.showChanges ? '_active' : '',
                      )}
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        setSelection({ index: entry.entryNumber ?? 0, showChanges: true })
                        e.stopPropagation()
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          setSelection({ index: entry.entryNumber ?? 0, showChanges: true })
                          e.stopPropagation()
                        }
                      }}
                    >
                      <span className="_maic-stats">
                        <span className="_change-count">{entry.changes.length}</span>
                        <span className="_price-delta">
                          {formatDeltaCurrency(entry.changes.reduce((sum, c) => sum + (c.consequences.priceDelta ?? 0), 0))}
                        </span>
                      </span>

                      <Icon
                        iconUrl={ChevronRightIcon}
                        size="default"
                        className={cx(
                          '_changes-icon _maic-changes',
                          entry.entryNumber === selection?.index && selection?.showChanges ? '_active' : '',
                        )}
                      />
                    </span>
                  )}
                </div>
              </Fragment>
            )
          })}
        </section>
        <section className="_details" data-testid={testIds.journal.details}>
          {!activeEntry && t('man-code.information-overlay.journal.select-entry', 'Select a journal entry to see details.')}
          {activeEntry && !selection?.showChanges && (
            <>
              <header>
                <span>
                  {/*<Icon iconUrl={UnknownIcon} title={t('man-code.information-overlay.journal.entries.type', 'Type')} size="default" />*/}
                </span>
                <span>
                  {collapseFpcChanges
                    ? t('man-code.information-overlay.journal.entries.code-or-path', 'Code / Path')
                    : t('man-code.information-overlay.journal.entries.path', 'Path')}
                </span>
                <span>
                  {collapseFpcChanges
                    ? t('man-code.information-overlay.journal.entries.family-or-value', 'Family / Value')
                    : t('man-code.information-overlay.journal.entries.value', 'Value')}
                </span>
                <span>{collapseFpcChanges ? t('man-code.information-overlay.journal.entries.variant', 'Variant') : undefined}</span>
                <span />
                <span>{collapseFpcChanges ? t('man-code.information-overlay.journal.entries.code', 'Code') : undefined}</span>
                <span>
                  {collapseFpcChanges
                    ? t('man-code.information-overlay.journal.entries.family-or-value', 'Family / Value')
                    : t('man-code.information-overlay.journal.entries.value', 'Value')}
                </span>
                <span>{collapseFpcChanges ? t('man-code.information-overlay.journal.entries.variant', 'Variant') : undefined}</span>
              </header>

              {activeEntry.groupedChanges.length === 0 && (
                <p data-testid={testIds.journal.state.empty}>
                  {t('man-code.information-overlay.journal.entries.no-changes', 'No changes recorded in this journal entry.')}
                </p>
              )}
              {activeEntry.groupedChanges.map((c) => {
                if (c.type === 'fpc') {
                  return <FPCCodeChangeEntry key={c.path} change={c} />
                }

                return (
                  <div key={c.path} className={cx('_row _other-change-entry')} data-testid={testIds.journal.entries.other}>
                    <span>
                      <HoverTooltip.span
                        className={cx('_change-type', `_type-${c.from?.op ?? 'unknown'}`)}
                        title={c.from?.op ?? t('shared:common.label.unknown')}
                      >
                        <Icon iconUrl={mapTypeToIcon(c.from?.op)} size="xs" />
                      </HoverTooltip.span>
                    </span>
                    <span className="_id">
                      <code>{c.path}</code>
                    </span>
                    <span>{pv(c.from) ?? <NotAvailable />}</span>
                    <span />
                    <span className="_change-cell">
                      <Icon iconUrl={ChangeIcon} size="default" />
                    </span>
                    <span />
                    <span>{pv(c.to) ?? <NotAvailable />}</span>
                    <span />
                  </div>
                )
              })}
            </>
          )}
          {activeEntry &&
            selection?.showChanges &&
            activeEntry.changes.length > 0 &&
            activeEntry.changes.map((change, idx) => (
              <div key={idx} className={cx('_detailed-change-entry')} data-testid={testIds.journal.entries.history}>
                <HistoryDetailList entry={change} />
              </div>
            ))}
        </section>
      </main>

      <footer>
        {!selection?.showChanges && (
          <Checkbox
            label={t('man-code.information-overlay.journal.collapse-fpc-changes', 'Collapse FPC Changes')}
            checked={collapseFpcChanges}
            onChange={() => {
              setCollapseFpcChanges(!collapseFpcChanges)
            }}
            data-testid={testIds.journal.collapseFpcChanges}
          />
        )}
      </footer>
    </>
  )
}
